// Purchase order helpers for the browser: lookups shared by the PO screens, and creating POs or
// adding lines with the rows shown at once (the server fills in the same defaults from shared/po.ts).
import { useMemo } from "react";
import { calc } from "../../../../shared/calc.ts";
import { planToday } from "../../../../shared/day.ts";
import { DEFAULTS, type Item, type Po, type PoLine, type Vendor, type VendorItem } from "../../../../shared/db.gen.ts";
import { draftFor, isOpen, lineDefaults, type PoStatus, validQty } from "../../../../shared/po.ts";
import { daysFrom } from "../../lib/format.ts";
import { mutateWithResult } from "../../lib/mutate.ts";
import { coverThroughFor } from "../../lib/planning.ts"; // Cover through ---
import { navigate, setSearch } from "../../lib/router.tsx";
import { type Change, store, useRows } from "../../lib/sync.ts";

export const STATUS_TONE: Record<PoStatus, "neutral" | "accent" | "ok" | "warn" | "danger"> = {
  draft: "neutral",
  sent: "accent",
  acknowledged: "accent",
  in_production: "accent",
  shipped: "warn",
  received: "ok",
  closed: "neutral",
  cancelled: "danger",
};

export const poPath = (id: string) => `/pos?open=${id}`;
export const vendorLabel = (v: Pick<Vendor, "name" | "short_name"> | undefined) => (v ? v.short_name || v.name : "Unknown vendor");

/** PO lines grouped by PO, in line order. */
export function useLinesByPo() {
  const lines = useRows("po_line");
  return useMemo(() => {
    const out = new Map<string, PoLine[]>();
    for (const l of lines) out.set(l.po_id, [...(out.get(l.po_id) ?? []), l]);
    for (const list of out.values()) list.sort((a, b) => a.position - b.position || a.created_at.localeCompare(b.created_at));
    return out;
  }, [lines]);
}

/** One vendor's links by item id. */
export function useVendorTerms(vendorId: string | undefined) {
  const links = useRows("vendor_item");
  return useMemo(() => new Map(links.filter((l) => l.vendor_id === vendorId).map((l) => [l.item_id, l])), [links, vendorId]);
}

/** HHL's default location: where a PO with no ship-to goes (the server picks the same). */
function hhlHome(): string | null {
  const hhl = (store.rows("location") as unknown as { id: string; kind: string; active: boolean; ignored: boolean; name: string }[])
    .filter((l) => l.kind === "hhl" && l.active && !l.ignored)
    .sort((a, b) => a.name.localeCompare(b.name))[0];
  return hhl?.id ?? null;
}

/** Where a new PO ships: the vendor's usual place, else HHL (the server picks the same). */
export function defaultShipTo(vendor: Pick<Vendor, "default_ship_to_location_id">): string | null {
  return vendor.default_ship_to_location_id ?? hhlHome();
}

/**
 * The open draft to add a vendor's lines to, for this ship-to and kind (shared/po.ts draftFor()): a make draft or one
 * shipping somewhere else never takes them. null: start one. `shipTo` undefined = where a new PO for it would ship.
 */
export function openDraftFor(vendor: Pick<Vendor, "id" | "default_ship_to_location_id">, shipTo: string | null | undefined, kind: Po["kind"]): Po | null {
  return draftFor(store.rows("po") as unknown as Po[], vendor.id, shipTo === undefined ? defaultShipTo(vendor) : shipTo, kind, hhlHome());
}

/** A line to add; a price or promised date given (Paste lines) wins over the vendor's. */
export type NewLine = { item: Item; link: VendorItem | undefined; qty?: number; unit_price?: number | null; promised_date?: string | null };

function lineChanges(poId: string, lines: NewLine[], firstPosition: number) {
  const now = new Date().toISOString();
  const args = lines.map((l, i) => {
    const qty = l.qty ?? validQty(l.link);
    return {
      id: crypto.randomUUID(),
      item_id: l.item.id,
      qty,
      position: firstPosition + i,
      ...lineDefaults(l.item, l.link, qty),
      ...(l.unit_price !== undefined ? { unit_price: l.unit_price } : {}),
      ...(l.promised_date ? { promised_date: l.promised_date } : {}),
    };
  });
  const changes: Change[] = args.map((a) => ({
    t: "po_line",
    id: a.id,
    row: { ...DEFAULTS.po_line, ...a, po_id: poId, created_at: now, updated_at: now },
  }));
  return { args, changes };
}

/** Creates a draft PO (optionally with lines) and opens it; it ships to `shipTo`, else where the vendor's POs go. */
export async function createPo(
  vendor: Vendor,
  lines: NewLine[] = [],
  kind: "purchase" | "service" = "purchase",
  shipTo?: string | null,
  /** `open: false`: made without opening it (several at once, from a list). */
  opts: { open?: boolean } = {},
) {
  const id = crypto.randomUUID();
  const now = new Date().toISOString();
  const { args, changes } = lineChanges(id, lines, 1);
  const row = {
    ...DEFAULTS.po,
    id,
    number: "PO-…",
    vendor_id: vendor.id,
    kind,
    ship_to_location_id: shipTo ?? defaultShipTo(vendor),
    currency: vendor.currency,
    payment_terms: vendor.payment_terms,
    incoterms: vendor.incoterms,
    deposit_pct: vendor.deposit_pct,
    cover_through: kind === "purchase" ? coverThroughFor(vendor.id) : null, // Cover through --- the person's day for this vendor
    created_at: now,
    updated_at: now,
  };
  const { ok } = await mutateWithResult(
    "po.create",
    {
      id,
      vendor_id: vendor.id,
      kind,
      lines: args,
      ...(shipTo ? { ship_to_location_id: shipTo } : {}),
      ...(row.cover_through ? { cover_through: row.cover_through } : {}),
    },
    [{ t: "po", id, row }, ...changes],
  );
  if (ok && opts.open !== false) location.pathname === "/pos" ? setSearch({ open: id }) : navigate(poPath(id));
  return ok ? id : null;
}

/**
 * Adds lines to a PO after its last one, one line per item (the server's addOrRaiseLines()): an item already on it has
 * its line raised by the quantity added, and an item twice is one line. Shown at once the same way (a sent PO's line
 * is the server's to raise or not, by its spec revision, so it shows when it lands).
 */
export async function addLines(po: Po, existing: PoLine[], lines: NewLine[]) {
  if (!lines.length) return false;
  const last = existing.reduce((n, l) => Math.max(n, l.position), 0);
  const { args } = lineChanges(po.id, lines, last + 1);
  const first = new Map<string, PoLine>();
  for (const l of [...existing].sort((x, y) => x.position - y.position)) if (!first.has(l.item_id)) first.set(l.item_id, l);
  const now = new Date().toISOString();
  const shown = new Map<string, Record<string, unknown> & { id: string; qty: number }>();
  const changes: Change[] = [];
  for (const a of args) {
    const prev = shown.get(a.item_id);
    if (prev) {
      prev.qty = Number(prev.qty) + a.qty;
      continue;
    }
    const on = po.sent_revision === null ? first.get(a.item_id) : undefined;
    const row = on ? { ...on, qty: Number(on.qty) + a.qty, updated_at: now } : { ...DEFAULTS.po_line, ...a, po_id: po.id, created_at: now, updated_at: now };
    shown.set(a.item_id, row);
    changes.push({ t: "po_line", id: row.id, row });
  }
  return (await mutateWithResult("po.add_lines", { po_id: po.id, lines: args }, changes)).ok;
}

/**
 * Lines onto a vendor's draft by the one rule every PO button follows (Mitch, 2026-11-04: "I don't want a thousand ways
 * to add a PO … deduplicated"): its open draft of this kind shipping to this place (openDraftFor()), an item already on
 * it raised, else a new draft. Nothing opens; the caller opens what comes back.
 */
export async function putOnDraft(
  vendor: Vendor,
  lines: NewLine[],
  opts: { kind?: "purchase" | "service"; shipTo?: string | null } = {},
): Promise<{ id: string; number: string; created: boolean } | null> {
  const kind = opts.kind ?? "purchase";
  const draft = openDraftFor(vendor, opts.shipTo, kind);
  if (draft) {
    const existing = (store.rows("po_line") as unknown as PoLine[]).filter((l) => l.po_id === draft.id);
    if (lines.length && !(await addLines(draft, existing, lines))) return null;
    return { id: draft.id, number: draft.number, created: false };
  }
  const id = await createPo(vendor, lines, kind, opts.shipTo, { open: false });
  return id ? { id, number: (store.row("po", id) as Po | undefined)?.number ?? "a new draft", created: true } : null;
}

/** "960", "1,440.5" or a sum ("40/500") → number; null when it isn't one. */
export const parseNumber = (text: string): number | null => calc(text);

/**
 * Days a PO is past the day it's needed by (0 when it isn't): sent and not received, closed or cancelled, with a
 * "Needed by" date before today. A draft can't be late: nobody has asked for it yet.
 */
export function daysLate(po: Pick<Po, "status" | "expected_arrive">, today = planToday()): number {
  if (!po.expected_arrive || po.status === "draft" || !isOpen(po.status)) return 0;
  return Math.max(0, daysFrom(String(po.expected_arrive).slice(0, 10), today));
}
